import * as R from "ramda";
import { Action, ActionFunction1 } from "redux-actions";
import { call, put, select } from "redux-saga/effects";
import { ActionCreator, IAction } from "reduxsauce";
import { Creators as AllCreators, Types as AllTypes } from "..";
import { IUser } from "../../App/reducer";
import {
  CourseChanges,
  IClientPatch,
  IPatchAction,
  IPatchPayload,
  IPatchSubstAction,
} from "../../components/course-author/helpers/patches";
import {
  createNewStyle,
  modifyBackgroundPatch,
  modifyCourseStylePatch,
  modifyLessCodePatch,
  modifylogoImagePatch,
  patchPayloadCreator,
} from "../../components/course-author/helpers/patches/creators";
import { CoursePlayer, ICourseStyle, IFullCourse } from "../../components/course-author/types";
import { config } from "../../config";
import { api } from "../../dependencies";
import { hasErrorRecords, resolveFullCoursePostSuccessResponseValueToCourseData } from "../../parsers/resolve";
import { IAutocompleteApi, IFileUploadApi, ILocalizedMessageApi } from "../../reducers/course-author/types";
import { SagaAction } from "../../sagas/types";
import * as selectors from "../../selectors/selectors";
import { fullCourseViews } from "../../utils/courseViews";
import * as courseHelpers from "../helpers/course";
import { SagaFunction } from "../types";
import { IAddImmersiveBgAction } from "./course-author";

export const Creators = (AllCreators as any).App.CourseAuthor;
const OrgCreators = (AllCreators as any).App.SelectOrganization;
export const Types = (AllTypes as any)._APP._COURSE_AUTHOR;

export type SagaFn<D = any, A = IAction> = (dependencies: D, action: A) => IterableIterator<any>;

export interface IFetchCourseFurtherActions {
  onSuccess: ActionCreator;
  onFailure: ActionCreator;
  withUpdate?: ActionCreator;
}

export const fetchFullCourse: (fa: IFetchCourseFurtherActions) => SagaFunction = furtherActions =>
  function* _fetchFullCourse(action: IAction): SagaAction {
    const accountId = yield select(selectors.accountId);
    const [coursesError, coursesResponse] = yield call(api.resolve.courseFull.post, accountId, [action.courseId]);
    if (coursesError) {
      yield put(
        furtherActions.onFailure({
          message: "Failed to fetch full course by id.",
        })
      );
    } else if (hasErrorRecords(coursesResponse.data)) {
      yield put(furtherActions.onFailure({ message: "Full course contained errors." }));
    } else {
      const data = resolveFullCoursePostSuccessResponseValueToCourseData(coursesResponse.data);
      yield put(
        furtherActions.onSuccess(
          data,
          {
            getEligibleEditors: api.autocomplete.getEligibleEditors(accountId, (data as IFullCourse).state.owner),
          } as IAutocompleteApi,
          {
            uploadFile: (file: File, onUploadProgress?: (progressEvent: ProgressEvent) => void) =>
              api.courseAuthor.uploadFile(file, accountId, onUploadProgress),
            uploadFileViaUrl: (link: string, onUploadProgress?: (progressEvent: ProgressEvent) => void) =>
              api.courseAuthor.uploadViaUrl(link, accountId, onUploadProgress),
          } as IFileUploadApi,
          {
            getExitCourseExplanation: api.messages.getExitCourseExplanation(accountId),
            getExitCourseScormExplanation: api.messages.getExitCourseScormExplanation(accountId),
          } as ILocalizedMessageApi
        )
      );
      if (furtherActions.withUpdate) {
        yield put(furtherActions.withUpdate(data));
      }
      const changesCount = yield select(selectors.changesCount);
      if (changesCount === 0) {
        yield put(Creators.fetchCourseChanges(data, action.courseId, 50, 0));
      }
    }
  };

export interface IResolveAuxiliaryCourseDataFurtherActions {
  onSuccess: ActionCreator;
  onFailure: ActionCreator;
}

export const resolveAuxiliaryCourseData: (
  fa: IResolveAuxiliaryCourseDataFurtherActions
) => SagaFunction = furtherActions =>
  function* _resolveAuxData(action): SagaAction {
    const accountId = yield select(selectors.accountId);
    const [resolveError, resolveResponse] = yield call(api.courseAuthor.resolve, action.requestData, accountId);

    if (resolveError) {
      yield put(
        furtherActions.onFailure({
          message: `Failed to resolve course data`,
        })
      );
    } else {
      const data = courseHelpers.zipFilterResponseData(action.requestData, resolveResponse);
      yield put(furtherActions.onSuccess(action.requestName, data));
    }
  };

export interface IPatchCourseFurtherActions {
  onSuccess: ActionCreator;
  onFailure: ActionCreator;
}

export const patchCourse: (fa: IPatchCourseFurtherActions) => SagaFunction = furtherActions =>
  function* _patchCourse(action: IAction): SagaAction {
    const { patchPayload } = action;
    const courseId: string = action.courseId || (yield select(selectors.currentCourseId));
    const accountId = yield select(selectors.accountId);
    const [patchesError] = yield call(api.courseAuthor.patchCourse, accountId, courseId, patchPayload);

    if (patchesError) {
      yield put(
        furtherActions.onFailure({
          message: `Failed to patch course ${courseId}`,
        })
      );
    } else {
      yield put(furtherActions.onSuccess(courseId));
      yield put(Creators.updateFullCourse(courseId));
    }
  };

export interface IUpdateFullCourseDataFurtherActions {
  onFailure: ActionCreator;
  onSuccess: ActionCreator;
  fetchCourse: {
    onSuccess: ActionFunction1<IFullCourse, Action<IFullCourse>>;
  };
}

export const updateFullCourseData: (
  furtherActions: IUpdateFullCourseDataFurtherActions
) => SagaFunction = furtherActions =>
  function* _updateFullCourse(action): SagaAction {
    const { courseId } = action;
    const accountId = yield select(selectors.accountId);
    const [coursesError, coursesResponse] = yield call(api.resolve.courseFull.post, accountId, [courseId]);

    if (!R.isNil(coursesError)) {
      yield put(
        furtherActions.onFailure({
          message: "Failed to fetch full course by id",
        })
      );
      return;
    }

    const fullCourse = resolveFullCoursePostSuccessResponseValueToCourseData(coursesResponse.data);

    if (R.isNil(fullCourse)) {
      yield put(
        furtherActions.onFailure({
          message: "Failed to fetch full course by id",
        })
      );
      return;
    }

    yield put(furtherActions.fetchCourse.onSuccess(fullCourse));

    let adminsData: Array<R.KeyValuePair<string, IUser>> = [];
    let changes: CourseChanges = [];
    let editorsData: Array<R.KeyValuePair<string, IUser>> = [];

    const adminsToResolve = fullCourse.state.admins.map(admin => `${admin}?display`);
    const [resolveAdminError, resolveAdminResponse] = yield call(api.courseAuthor.resolve, adminsToResolve, accountId);

    if (resolveAdminError) {
      yield put(
        furtherActions.onFailure({
          message: `Failed to resolve admins data`,
        })
      );
      return;
    }

    if (resolveAdminResponse) {
      adminsData = courseHelpers.zipFilterResponseData(adminsToResolve, resolveAdminResponse);
    }

    const changesCount = yield select(selectors.changesCount);

    const changeIds = courseHelpers.generateChangeIds(fullCourse.versionId, courseId, changesCount + 1);
    const [resolveChangesError, resolveChangesResponse] = yield call(api.courseAuthor.resolve, changeIds, accountId);

    if (resolveChangesError) {
      yield put(
        furtherActions.onFailure({
          message: "Failed to resolve course changes",
        })
      );
      return;
    } else {
      changes = resolveChangesResponse.data.map(R.prop("value"));
    }

    const editorIds: string[] = courseHelpers.uniqCreatorIds(changes);
    if (editorIds.length > 0) {
      const [resolveEditorsError, resolveEditorsResponse] = yield call(api.courseAuthor.resolve, editorIds, accountId);

      if (resolveEditorsError) {
        yield put(
          furtherActions.onFailure({
            message: `Failed to resolve course change authors`,
          })
        );
        return;
      } else {
        editorsData = courseHelpers.zipFilterResponseData(editorIds, resolveEditorsResponse);
      }
    }

    yield put(furtherActions.onSuccess(fullCourse, adminsData, changes, editorsData));
  };

export interface ICreateStyleFurtherActions {
  onSuccess: ActionCreator;
  onFailure: ActionCreator;
}

export const createStyle: (fa: ICreateStyleFurtherActions) => SagaFunction = furtherActions =>
  function* _createStyle(action): SagaAction {
    const accountId = yield select(selectors.accountId);
    const orgId = yield select(selectors.organizationId);
    const courseId = yield select(selectors.currentCourseId);
    const [error, response] = yield call(api.courseAuthor.createStyle, accountId, orgId, action.stylePayload);

    if (error) {
      yield put(
        furtherActions.onFailure({
          message: `Failed to create new style for course crs.${courseId}`,
        })
      );
    } else {
      yield put(furtherActions.onSuccess(response.data));
    }
  };

export interface IPatchStyleFurtherActions {
  onSuccess: ActionCreator;
  onFailure: ActionCreator;
}

export const patchStyle: (fa: IPatchStyleFurtherActions) => SagaFunction = furtherActions =>
  function* _patchStyle(action): SagaAction {
    const accountId = yield select(selectors.accountId);
    const orgId = yield select(selectors.organizationId);
    const [error, response] = yield call(
      api.courseAuthor.patchStyle,
      accountId,
      orgId,
      action.styleId,
      action.patchPayload
    );

    if (error) {
      yield put(
        furtherActions.onFailure({
          message: `Failed to patch style sty.${action.styleId}`,
        })
      );
    } else {
      yield put(furtherActions.onSuccess(response.data));
    }
  };

export interface ICloneCourseFutherActions {
  patchStyleFurtherActions: IPatchCoursePlayerFurtherActions;
  onFailure: ActionCreator;
  onSuccess: ActionCreator;
}

export interface ICloneCourseAction extends IAction {
  course: IFullCourse;
  organizationId: string;
  navAction: () => void;
}

export const cloneCourse: (fa: ICloneCourseFutherActions) => SagaFunction<ICloneCourseAction> = furtherActions =>
  function* _cloneCourse(action): SagaAction {
    const { course, organizationId } = action;
    const activeUser = yield select(selectors.accountId);
    const newCourse = {
      ...courseHelpers.cloneCourse(course, activeUser, organizationId, " (Clone)"),
      versionId: 0,
    };

    const [createCourseError, createCourseResp] = yield call(api.courses.create, newCourse, activeUser);
    if (createCourseError) {
      return yield put(furtherActions.onFailure());
    }

    const clonedCourseId = createCourseResp.data.value;

    if (course.state.style !== null) {
      const [createStyleError, createStyleResp] = yield call(
        api.courseAuthor.createStyle,
        activeUser,
        organizationId,
        createNewStyle(clonedCourseId, organizationId)
      );
      if (createStyleError) {
        return yield put(
          furtherActions.onFailure({
            message: "Failed to create style from course",
          })
        );
      }
      const styleId = createStyleResp.data.value;
      const courseStyle: ICourseStyle = createStyleResp.data.bindings[`sty.${styleId}`];
      const newCourseStyle = yield call(
        courseHelpers.createStyleForCourse,
        newCourse.state.coursePlayer,
        newCourse.state.background
      );
      const lessCodePatch = yield call(modifyLessCodePatch, activeUser, courseStyle, newCourseStyle);
      const stylePatch = yield call(patchPayloadCreator, courseStyle.versionId, lessCodePatch);

      const patchStyleSaga = patchStyle(furtherActions.patchStyleFurtherActions.patchStyle);
      yield call(patchStyleSaga, Creators.patchStyle(styleId, stylePatch));

      const patchPayload = patchPayloadCreator(
        newCourse.versionId,
        modifyCourseStylePatch(activeUser, newCourse, styleId)
      );
      yield call(
        patchCourse(furtherActions.patchStyleFurtherActions.patchCourse),
        Creators.patchCourse(patchPayload, clonedCourseId)
      );
      yield put(furtherActions.patchStyleFurtherActions.onSuccess({}));
    }

    yield put(furtherActions.onSuccess({}));
    yield action.navAction();
    yield put(OrgCreators.updateCoursesAmount(1));
  };

export interface IImmersiveBgFurtherActions {
  onFailure: ActionCreator;
  onSuccess: ActionCreator;
  patchCourse: IPatchCourseFurtherActions;
}

export const addImmersiveBackground: (
  fa: IImmersiveBgFurtherActions
) => SagaFunction<IAddImmersiveBgAction> = furtherActions =>
  function* _addImmersiveBackground(action): SagaAction {
    const activeUser = yield select(selectors.accountId);
    const activeOrg = yield select(selectors.organizationId);

    const existingStyleId = fullCourseViews.style(action.course);
    const needsStyle = R.isNil(existingStyleId);
    const files = action.files || [];

    const [uploadError, uploadResponse] =
      files.length > 0
        ? yield call(api.courseAuthor.uploadFile, files[0], activeUser)
        : yield call(api.courseAuthor.uploadViaUrl, action.link, activeUser);

    if (uploadError) {
      alert("Failed to upload new file");
      yield put(
        furtherActions.onFailure({
          message: `Failed to upload new file: ${(files[0] && files[0].name) || action.link}`,
        })
      );
      return;
    }

    const { mediaId }: { mediaId: string } = uploadResponse.data[0] || uploadResponse.data;

    const [createStyleError, createStyleResponse] = needsStyle
      ? yield call(api.courseAuthor.createStyle, activeUser, activeOrg, createNewStyle(action.courseId, activeOrg))
      : [null, null];

    if (createStyleError) {
      yield put(
        furtherActions.onFailure({
          message: `Failed to create new style for crs.${action.courseId}`,
        })
      );
      return;
    }

    const styleId = existingStyleId || createStyleResponse.data.value;
    const newStyle: ICourseStyle = needsStyle ? createStyleResponse.data.bindings[`sty.${styleId}`] : null;
    const [existingStyleError, existingStyleResponse] = needsStyle
      ? [null, null]
      : yield call(api.resolve.style.post, activeUser, [styleId], activeOrg);

    if (
      !R.isNil(existingStyleError) ||
      (!R.isNil(existingStyleResponse) && hasErrorRecords(existingStyleResponse.data))
    ) {
      yield put(
        furtherActions.onFailure({
          message: `Error retrieving existing style ${styleId}`,
        })
      );
      return;
    }

    const style: ICourseStyle = newStyle || existingStyleResponse.data[0].value;

    const imgId = mediaId.slice(4);
    const { state }: IFullCourse = yield select(selectors.currentCourse);
    const imgUrl = courseHelpers.createImageUrl(config.saltcomplianceApiRoot, imgId);
    const lessCodePatch = modifyLessCodePatch(
      activeUser,
      style,
      courseHelpers.createStyleForCourse(state.coursePlayer, imgUrl)
    );
    const [patchStyleError] = yield call(
      api.courseAuthor.patchStyle,
      activeUser,
      activeOrg,
      styleId,
      patchPayloadCreator(style.versionId, [modifylogoImagePatch(activeUser, style, imgId), lessCodePatch])
    );

    if (patchStyleError) {
      yield put(
        furtherActions.onFailure({
          message: `Failed to patch style sty.${styleId} with new background image`,
        })
      );
      return;
    }

    const coursePatches = createStyleResponse
      ? [
          modifyCourseStylePatch(activeUser, action.course, styleId),
          modifyBackgroundPatch(activeUser, action.course, imgUrl),
        ]
      : [modifyBackgroundPatch(activeUser, action.course, imgUrl)];

    const patchPayload = patchPayloadCreator(action.course.versionId, coursePatches);

    yield* patchCourse(furtherActions.patchCourse)(Creators.patchCourse(patchPayload));

    yield put(furtherActions.onSuccess());
  };

export const deleteImmersiveBackground: (fa: IImmersiveBgFurtherActions) => SagaFunction<IAction> = furtherActions =>
  function* _deleteImmersiveBackground(): SagaAction {
    const course = yield select(selectors.currentCourse);
    const courseId = yield select(selectors.currentCourseId);
    const existingStyleId = fullCourseViews.style(course);

    if (R.isNil(course.state.background) || R.isNil(existingStyleId)) {
      alert("Error deleting background image");
      const message = R.isNil(course.state.background)
        ? `Course ${courseId} does not have a background image`
        : R.isNil(existingStyleId)
        ? `Could not found style for course ${courseId}`
        : "";
      yield put(furtherActions.onFailure({ message }));
      return;
    }

    const activeUser = yield select(selectors.accountId);
    const activeOrg = yield select(selectors.organizationId);

    const [existingStyleError, existingStyleResponse] = yield call(
      api.resolve.style.post,
      activeUser,
      [existingStyleId],
      activeOrg
    );

    if (
      !R.isNil(existingStyleError) ||
      (!R.isNil(existingStyleResponse) && hasErrorRecords(existingStyleResponse.data))
    ) {
      yield put(
        furtherActions.onFailure({
          message: `Error retrieving existing style ${existingStyleId}`,
        })
      );
      return;
    }

    const style: ICourseStyle = existingStyleResponse.data[0].value;

    const lessCodePatch = modifyLessCodePatch(
      activeUser,
      style,
      courseHelpers.createStyleForCourse(course.state.coursePlayer, null)
    );
    const [patchStyleError] = yield call(
      api.courseAuthor.patchStyle,
      activeUser,
      activeOrg,
      existingStyleId,
      patchPayloadCreator(style.versionId, [modifylogoImagePatch(activeUser, style, null), lessCodePatch])
    );

    if (patchStyleError) {
      yield put(
        furtherActions.onFailure({
          message: `Failed to patch style sty.${existingStyleId} with new background image`,
        })
      );
      return;
    }

    const patchPayload = patchPayloadCreator(course.versionId, [modifyBackgroundPatch(activeUser, course, null)]);

    yield* patchCourse(furtherActions.patchCourse)(Creators.patchCourse(patchPayload));

    yield put(furtherActions.onSuccess());
  };

export interface IPatchCoursePlayerFurtherActions extends IImmersiveBgFurtherActions {
  patchStyle: IPatchStyleFurtherActions;
}

export interface IPatchCoursePlayerAction extends IAction {
  courseId: string;
  patchPayload: IPatchPayload;
}

export const patchCoursePlayer: (fa: IPatchCoursePlayerFurtherActions) => SagaFunction<IPatchCoursePlayerAction> = fa =>
  function* _patchCoursePlayer(action): SagaAction {
    const accountId = yield select(selectors.accountId);
    const orgId = yield select(selectors.organizationId);
    const coursePlayerValue = (action.patchPayload.clientPatches[0].patch.action as IPatchSubstAction<string>)
      .y as CoursePlayer;
    const fullCourse: IFullCourse = yield select(selectors.currentCourse);
    const {
      state: { style, background },
    } = fullCourse;
    const patchStyleSaga = patchStyle(fa.patchStyle);
    if (style) {
      const [err, resp] = yield call(api.resolve.style.post, accountId, [style], orgId);
      if (err || !resp?.data?.[0]?.value) {
        alert(
          "Failed to fetch style from course or you don't have the enough access to this course. Ask an administrator."
        );
        return yield put(fa.onFailure({ message: "Failed to fetch style from course" }));
      }
      const courseStyle = resp.data[0].value;
      const newCourseStyle = yield call(courseHelpers.createStyleForCourse, coursePlayerValue, background);
      const lessCodePatch = yield call(modifyLessCodePatch, accountId, courseStyle, newCourseStyle);
      const stylePatch = yield call(patchPayloadCreator, courseStyle.versionId, lessCodePatch);
      yield call(patchStyleSaga, Creators.patchStyle(style, stylePatch));
    } else {
      const [err, resp] = yield call(
        api.courseAuthor.createStyle,
        accountId,
        orgId,
        createNewStyle(action.courseId, orgId)
      );
      if (err) {
        return yield put(fa.onFailure({ message: "Failed to create style from course" }));
      }
      const styleId = resp.data.value;
      const courseStyle: ICourseStyle = resp.data.bindings[`sty.${styleId}`];
      const newCourseStyle = yield call(courseHelpers.createStyleForCourse, coursePlayerValue, background);
      const lessCodePatch = yield call(modifyLessCodePatch, accountId, courseStyle, newCourseStyle);
      const stylePatch = yield call(patchPayloadCreator, courseStyle.versionId, lessCodePatch);
      fullCourse.versionId += 1;
      const courseStylePatch = modifyCourseStylePatch(accountId, fullCourse, styleId);
      action.patchPayload.clientPatches.push(courseStylePatch);
      yield call(patchStyleSaga, Creators.patchStyle(styleId, stylePatch));
    }

    yield call(patchCourse(fa.patchCourse), Creators.patchCourse(action.patchPayload));

    yield put(fa.onSuccess({}));
  };

export interface IMigrateContentToHtmlFurtherActions {
  onSuccess: ActionCreator;
  onFailure: ActionCreator;
}

export const migrateContentToHtml: (fa: IMigrateContentToHtmlFurtherActions) => SagaFunction = fa =>
  function* _migrateContentToHtml(action: IAction): SagaAction {
    const accountId: string = yield select(selectors.accountId);
    const organizationId: string = yield select(selectors.organizationId);
    const currentCourse: IFullCourse = yield select(selectors.currentCourse);
    const courseId: string = yield select(selectors.currentCourseId);
    const backupCourse: IFullCourse = courseHelpers.cloneCourse(currentCourse, accountId, organizationId, " (Backup)");
    const [backupErr] = yield call(api.courses.create, backupCourse, accountId);
    if (backupErr) {
      return yield put(fa.onFailure({ message: "Failed to create course backup." }));
    }
    yield put(OrgCreators.updateCoursesAmount(1));
    const [migrationError, migrationResponse] = yield call(api.courseAuthor.migrateContentToHtml, courseId, accountId);
    const message = "Failed to migrate course content from markdown to html";
    if (migrationError) {
      yield put(
        fa.onFailure({
          message,
        })
      );
    } else if (migrationResponse.status !== 200) {
      yield put(
        fa.onFailure({
          message,
        })
      );
    } else {
      const course = migrationResponse.data as IFullCourse;
      yield put(fa.onSuccess(course));
      yield put(Creators.updateFullCourse(courseId));
    }
  };

export interface IFetchCourseChangesFurtherActions {
  onSuccess: ActionCreator;
  onFailure: ActionCreator;
}

interface IFetchCourseChangesAction extends IAction {
  course: IFullCourse;
  courseId: string;
}

export const fetchCourseChanges: (
  fa: IFetchCourseChangesFurtherActions
) => SagaFunction<IFetchCourseChangesAction> = furtherActions =>
  function* _fetchCourseChanges(action: IFetchCourseChangesAction): SagaAction {
    const { course, courseId, offset, count } = action;
    const accountId = yield select(selectors.accountId);
    const lastVersionId = course.versionId;
    const courseIds = new Array(lastVersionId)
      .fill(lastVersionId)
      .map((_, step) => `${courseId}?c${lastVersionId - step}`);
    const [resolveError, resolveResponse] = yield call(
      api.resolve.courseChanges.post,
      accountId,
      courseIds.slice(offset, offset + count)
    );

    if (resolveError) {
      yield put(
        furtherActions.onFailure({
          message: `Failed to resolve course changes`,
        })
      );
    } else {
      const { data } = resolveResponse;
      const changes = data.map(R.prop("value"));

      const requestData: string[] = R.compose<Array<IClientPatch<IPatchAction>>, string[], string[]>(
        R.uniq,
        R.map<IClientPatch<IPatchAction>, string>(change => `acc.${change.createdBy}?display`)
      )(changes);

      yield put(Creators.resolveAuxiliaryData("editors", requestData));
      yield put(furtherActions.onSuccess(changes));
    }
  };
